"use strict";Object.defineProperty(exports, "__esModule", {value: true}); function _nullishCoalesce(lhs, rhsFn) { if (lhs != null) { return lhs; } else { return rhsFn(); } }/* Renders {{name}} and {{name | fallback}} message templates. Broadcast
 * campaigns and saved message templates share it, so a template renders the
 * same everywhere. It mirrors the Go package apps/backend/pkg/template; both
 * are checked against apps/backend/pkg/template/testdata/cases.json, so
 * change them together. */

const TOKEN_PATTERN = /\{\{([^{}]*)\}\}/g
const NAME_PATTERN = /^[A-Za-z0-9_]+$/
const VARIABLE_NAME_PATTERN = /^[a-z0-9_]+$/

/* The longest variable name a saved template body may use. */
 const MAX_VARIABLE_NAME = 64; exports.MAX_VARIABLE_NAME = MAX_VARIABLE_NAME

// ASCII-only, matching the Go renderer's trim set (" \t\n\r\f\v"). Two
// index scans keep it linear; a regex suffix match is quadratic on long
// internal runs of spaces.
const isTrimSpace = (code) => code === 32 || (code >= 9 && code <= 13)

const trimSpace = (value) => {
  let start = 0
  let end = value.length
  while (start < end && isTrimSpace(value.charCodeAt(start))) start++
  while (end > start && isTrimSpace(value.charCodeAt(end - 1))) end--
  return value.slice(start, end)
}

const valueOf = (values, name) =>
  values && Object.prototype.hasOwnProperty.call(values, name) ? _nullishCoalesce(values[name], () => ( '')) : ''









/* Substitutes {{name}} and {{name | fallback}} tokens. A missing or blank
 * value uses the fallback; without one the token stays in place and is
 * reported as unresolved. Values are inserted literally, never re-rendered. */
 const renderTemplate = (template, values) => {
  let rendered = ''
  let last = 0
  const unresolved = []
  for (const match of template.matchAll(TOKEN_PATTERN)) {
    const index = _nullishCoalesce(match.index, () => ( 0))
    const token = match[0]
    const inner = _nullishCoalesce(match[1], () => ( ''))
    const pipe = inner.indexOf('|')
    const name = trimSpace(pipe < 0 ? inner : inner.slice(0, pipe))
    const value = valueOf(values, name)
    rendered += template.slice(last, index)
    if (!NAME_PATTERN.test(name)) {
      rendered += token
      unresolved.push(name)
    } else if (trimSpace(value) !== '') {
      rendered += value
    } else if (pipe >= 0) {
      rendered += trimSpace(inner.slice(pipe + 1))
    } else {
      rendered += token
      unresolved.push(name)
    }
    last = index + token.length
  }
  rendered += template.slice(last)
  return { rendered, unresolved }
}; exports.renderTemplate = renderTemplate

/* Length in Unicode code points, as the backend counts it. */
 const renderLength = (value) => [...value].length; exports.renderLength = renderLength













/* The template's tokens in order, read with renderTemplate's rules. */
 const templateTokens = (template) => {
  const tokens = []
  for (const match of template.matchAll(TOKEN_PATTERN)) {
    const inner = _nullishCoalesce(match[1], () => ( ''))
    const pipe = inner.indexOf('|')
    const name = trimSpace(pipe < 0 ? inner : inner.slice(0, pipe))
    tokens.push({
      name,
      fallback: pipe < 0 ? null : trimSpace(inner.slice(pipe + 1)),
      index: _nullishCoalesce(match.index, () => ( 0)),
      text: match[0],
      isValidName: NAME_PATTERN.test(name),
    })
  }
  return tokens
}; exports.templateTokens = templateTokens

/* The token names a saved template body can't use, once per occurrence:
 * names must be lowercase slugs of at most MAX_VARIABLE_NAME characters. */
 const invalidTokenNames = (body) =>
  exports.templateTokens.call(void 0, body)
    .map(token => token.name)
    .filter(name => exports.renderLength.call(void 0, name) > exports.MAX_VARIABLE_NAME || !VARIABLE_NAME_PATTERN.test(name)); exports.invalidTokenNames = invalidTokenNames

// The whitespace normalizeBody trims and replaces in names: ASCII whitespace
// plus Unicode's spaces, the same list as the Go package's isNormalizeSpace.
const isNormalizeSpace = (code) =>
  code === 32 || (code >= 9 && code <= 13)
  || code === 0x85 || code === 0xa0 || code === 0x1680 || (code >= 0x2000 && code <= 0x200a)
  || code === 0x2028 || code === 0x2029 || code === 0x202f || code === 0x205f || code === 0x3000 || code === 0xfeff

/* One variable name, standardized as normalizeBody does inside a token:
 * "Appt Date" → "appt_date". */
 const normalizeVariableName = (name) => {
  let start = 0
  let end = name.length
  while (start < end && isNormalizeSpace(name.charCodeAt(start))) start++
  while (end > start && isNormalizeSpace(name.charCodeAt(end - 1))) end--
  let output = ''
  let pendingUnderscore = false
  for (let i = start; i < end; i++) {
    const code = name.charCodeAt(i)
    if (isNormalizeSpace(code) || code === 45 /* - */ || code === 95 /* _ */) {
      pendingUnderscore = true
      continue
    }
    if (pendingUnderscore) {
      output += '_'
      pendingUnderscore = false
    }
    // ASCII letters only, as in Go: other letters stay for validation to refuse.
    output += code >= 65 && code <= 90 ? String.fromCharCode(code + 32) : name[i]
  }
  return pendingUnderscore ? output + '_' : output
}; exports.normalizeVariableName = normalizeVariableName

/* Standardizes every token's name, so "{{First Name}}" or "{{TEAM-NAME}}"
 * saves as "{{first_name}}" or "{{team_name}}": trims whitespace (Unicode
 * spaces too, such as a non-breaking space pasted from Word), lowercases
 * ASCII letters, turns inner whitespace and hyphens into "_", and collapses
 * repeated "_". A token is rewritten as {{name}} or {{name | fallback}}, with
 * the fallback trimmed as the renderer trims it. Text outside tokens, and
 * anything that isn't a token, is left as is. Rendering doesn't change: only
 * saving standardizes. Mirrors the Go package's NormalizeBody. */
 const normalizeBody = (body) =>
  body.replace(TOKEN_PATTERN, (_token, inner) => {
    const pipe = inner.indexOf('|')
    const name = exports.normalizeVariableName.call(void 0, pipe < 0 ? inner : inner.slice(0, pipe))
    if (pipe < 0) return `{{${name}}}`
    const fallback = trimSpace(inner.slice(pipe + 1))
    return fallback ? `{{${name} | ${fallback}}}` : `{{${name} |}}`
  }); exports.normalizeBody = normalizeBody
